import { render } from "vike/abort";
import CacheStatsPageEntryPoint from "@/components/[Entry]/CacheStatsEntryPoint";
import FundDetailPageEntryPoint from "@/components/[Entry]/FundDetailPageEntryPoint";
import FundListPageEntryPoint from "@/components/[Entry]/FundListPageEntryPoint";
import { pageQueryToPageDetails } from "@/utils/pageQueryToPageDetails";
import { parseQueryParamsFromUrl } from "@/utils/queryParams";
import { usePageContext } from "@/utils/usePageContext";
import type { ApiClientResponse } from "@/services/api/client";
import type { ShareClassDashboardEndpoint } from "@/services/server/v1/getShareClassDashboard";
import type { FilterOptions } from "@/types/FilterOptions";
import type { QueryParamsObj } from "@/types/UrlQueries";
import type { ShareClassListEndpoint } from "@/types/endpoints/ShareClassListEndpoint";
import ComparePageEntryPoint from "@/components/[Entry]/CompareEntryPoint";

const NotFound = () => {
	return (
		<div>
			<h3>404 Not Found</h3>
		</div>
	);
};

export default function Page() {
	const pageContext =
		usePageContext<
			ApiClientResponse<ShareClassListEndpoint["GET"]["response"]["body"] | ShareClassDashboardEndpoint["GET"]["response"]["body"]>
		>();

	if (pageContext.data.isErr) {
		throw render(
			pageContext.data.status as 401 | 403 | 404 | 410 | 429 | 500 | 503,
			`${pageContext.data.err.msg}, stack: ${pageContext.data.err.stack}`
		);
	}

	let instanceConfig;

	const data = pageContext.data?.body;
	if (data) {
		instanceConfig = data.instanceConfig;
	}

	const disableListPage = Boolean(instanceConfig?.misc?.disableListPage);
	const searchList = instanceConfig?.searchList?.filter?.items;
	const collapseAll = instanceConfig?.searchList?.filter?.collapseAllFields;
	const allFilters = instanceConfig?.filters || {};

	const filters = Object.keys(allFilters)
		.filter((key) => {
			return searchList?.includes(key);
		})
		.reduce<Record<string, FilterOptions>>((acc, key) => {
			acc[key] = { ...allFilters[key] };
			if (collapseAll) {
				acc[key].collapsed = true;
			}
			return acc;
		}, {});

	const query = pageContext.query || parseQueryParamsFromUrl(pageContext.urlOriginal);
	const { internalPageName } = pageQueryToPageDetails(query as QueryParamsObj);

	if (internalPageName === "Compare") {
		return <ComparePageEntryPoint />;
	}

	if (internalPageName === "CacheStats") {
		return <CacheStatsPageEntryPoint />;
	}

	if (internalPageName === "FundListView") {
		if (disableListPage) {
			return <NotFound />;
		}
		return <FundListPageEntryPoint filters={filters} />;
	}

	if (internalPageName === "FundDetailView") {
		return <FundDetailPageEntryPoint />;
	}

	return <NotFound />;
}
